/**
 * Boot-failure diagnostics for src/main.ts.
 *
 * ZERO imports on purpose: main.ts runs before the federation import map exists, so it
 * cannot pull `@env`, `@cbre/*` or any other shared package - and when boot has failed,
 * dynamic imports are the very thing that may be broken.
 *
 * Until 2026-09 a failed boot rendered the static "MarketIQ could not start" view and
 * logged to the console only. Angular, RUM and the Logs SDK never start on such a load,
 * so the failures were invisible: the Datadog synthetic login test failed on that page
 * for two weeks with no telemetry naming the request that broke. Two channels now, both
 * best-effort and never throwing:
 *
 *  1. an immediate beacon straight to the Datadog browser-logs intake, using the public
 *     client token that replaceToken.js injects into index.html at build time
 *     (<meta name="miq-dd-client-token">, <meta name="miq-dd-env">)
 *  2. a record in sessionStorage that DatadogLoaderService forwards through the normal
 *     Logs SDK once a later load succeeds ("Try again", refresh, next navigation)
 */

export const BOOT_FAILURE_STORAGE_KEY = 'miq_boot_failure';
export const DD_CLIENT_TOKEN_META = 'miq-dd-client-token';
export const DD_ENV_META = 'miq-dd-env';
const DD_LOGS_INTAKE = 'https://browser-intake-datadoghq.com/api/v2/logs';
const MAX_STACK_CHARS = 2000;

const getDatadogServiceName = (envName?: string): string => {
  if (!envName || envName === 'prod') {
    return 'cep-miq-frontend';
  }

  return `cep-miq-frontend-${envName}`;
};

export interface BootFailureReport {
  message: string;
  status: 'error';
  service: string;
  date: number;
  env?: string;
  boot_error: { name: string; message: string; stack?: string };
  /** Path only - the SSO callback URL carries a single-use authorization code. */
  url_path: string;
  referrer_origin: string;
  online: boolean;
  sw_controlled: boolean;
  navigation_type?: string;
  since_navigation_ms: number;
  synthetics_test_id?: string;
  synthetics_result_id?: string;
}

const readMeta = (name: string): string | undefined => {
  try {
    const content = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)?.content?.trim();
    return content || undefined;
  } catch {
    return undefined;
  }
};

const describeError = (error: unknown): BootFailureReport['boot_error'] => {
  if (error instanceof Error) {
    return { name: error.name, message: error.message, stack: error.stack?.slice(0, MAX_STACK_CHARS) };
  }
  return { name: typeof error, message: String(error) };
};

const originOf = (url: string): string => {
  try {
    return url ? new URL(url).origin : '';
  } catch {
    return '';
  }
};

export function buildBootFailureReport(error: unknown, now: number = Date.now()): BootFailureReport {
  const globals = globalThis as unknown as Record<string, unknown>;
  const navigation = performance.getEntriesByType?.('navigation')?.[0] as PerformanceNavigationTiming | undefined;
  const envName = readMeta(DD_ENV_META);
  return {
    message: 'MIQ bootstrap failed',
    status: 'error',
    service: getDatadogServiceName(envName),
    date: now,
    env: envName,
    boot_error: describeError(error),
    url_path: globalThis.location?.pathname ?? '',
    referrer_origin: originOf(document.referrer),
    online: navigator.onLine,
    sw_controlled: !!navigator.serviceWorker?.controller,
    navigation_type: navigation?.type,
    since_navigation_ms: Math.round(performance.now()),
    // Injected by the Datadog Synthetics browser; lets a failed test run be joined to this log.
    synthetics_test_id:
      typeof globals['_DATADOG_SYNTHETICS_PUBLIC_ID'] === 'string'
        ? (globals['_DATADOG_SYNTHETICS_PUBLIC_ID'] as string)
        : undefined,
    synthetics_result_id:
      typeof globals['_DATADOG_SYNTHETICS_RESULT_ID'] === 'string'
        ? (globals['_DATADOG_SYNTHETICS_RESULT_ID'] as string)
        : undefined,
  };
}

/** Immediate delivery. Returns false when there is no client token or the browser refused. */
export function sendBootFailureBeacon(
  report: BootFailureReport,
  clientToken: string | undefined = readMeta(DD_CLIENT_TOKEN_META)
): boolean {
  if (!clientToken) {
    return false;
  }
  try {
    const url = `${DD_LOGS_INTAKE}?ddsource=browser&dd-api-key=${encodeURIComponent(clientToken)}&dd-evp-origin=browser`;
    // text/plain keeps this a simple request (no CORS preflight), same as the Logs SDK.
    const body = new Blob([JSON.stringify(report)], { type: 'text/plain' });
    if (typeof navigator.sendBeacon === 'function') {
      return navigator.sendBeacon(url, body);
    }
    void fetch(url, { method: 'POST', body, mode: 'cors', keepalive: true }).catch(() => undefined);
    return true;
  } catch {
    return false;
  }
}

export function storeBootFailureReport(report: BootFailureReport): void {
  try {
    sessionStorage.setItem(BOOT_FAILURE_STORAGE_KEY, JSON.stringify(report));
  } catch {
    // Storage unavailable - the beacon is the only channel.
  }
}

/** Returns the pending record (once) for the app shell to forward through the Logs SDK. */
export function takeBootFailureReport(): BootFailureReport | null {
  try {
    const raw = sessionStorage.getItem(BOOT_FAILURE_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    sessionStorage.removeItem(BOOT_FAILURE_STORAGE_KEY);
    return JSON.parse(raw) as BootFailureReport;
  } catch {
    return null;
  }
}

/** Entry point for main.ts. Never throws: diagnostics must not mask the retry view. */
export function reportBootFailure(error: unknown): void {
  try {
    const report = buildBootFailureReport(error);
    storeBootFailureReport(report);
    sendBootFailureBeacon(report);
  } catch {
    // Nothing else to fall back to.
  }
}
